import { BASE_URL } from "../constants";
import type { AuthToken } from "../types/auth";
import { getUserIdFromAccessToken } from "./helpers";

type RefreshTokenPayload = {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number | null;
};

export function mapRefreshPayloadToAuthToken(data: RefreshTokenPayload): AuthToken {
  const access_token = data.access_token ?? "";
  const id_token = data.id_token ?? "";
  const refresh_token = data.refresh_token ?? "";
  const userId = getUserIdFromAccessToken(access_token);
  return {
    id_token,
    access_token,
    refresh_token,
    expires_in: data.expires_in ?? null,
    userId,
  };
}

/**
 * Used by the axios 401 interceptor to avoid importing authService/apiHelper (circular deps).
 */
export async function fetchRefreshedAuthToken(refreshToken: string): Promise<AuthToken> {
  const trimmed = typeof refreshToken === "string" ? refreshToken.trim() : "";
  if (!trimmed) {
    throw new Error("No refresh token");
  }
  const res = await fetch(`${BASE_URL}/auth/refresh-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refreshToken: trimmed }),
  });
  const json = (await res.json()) as {
    status?: string;
    message?: string;
    data?: RefreshTokenPayload;
  };
  const status = json.status?.toUpperCase();
  if (!res.ok || status === "ERROR") {
    throw new Error(json.message || "Token refresh failed");
  }
  const data = json.data;
  if (!data?.access_token || !data?.refresh_token) {
    throw new Error(json.message || "Token refresh failed");
  }
  return mapRefreshPayloadToAuthToken(data);
}
